import {Link, Show, Text} from 'platform/foundation';
import {cn, cva} from 'platform/shared';
import {match} from 'ts-pattern';

import {isNotNil} from 'ramda';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {OptionStatusIndicator} from '../../types/OptionTypeBase';
import {StatusIndicatorDot} from '../Choice/utils/StatusIndicatorDot';
import {getHighlightsFromText} from './utils/getHighlightsFromText';

export interface MentionsTextProps extends TestIdProps {
  value: string | Nullish;
  /** Per-mentioned-user status indicators (e.g. user-absence red dot), keyed by user id. */
  statusIndicatorById?: Map<string, OptionStatusIndicator>;
}

const textWrapperVariants = cva('whitespace-pre-line');
const mentionHighlightVariants = cva(
  'inline-flex items-center gap-1 bg-[var(--color-palettes-blue-30-100)]'
);

export function MentionsText(props: MentionsTextProps) {
  const textHighlights = getHighlightsFromText(props.value ?? '');

  return (
    <div className={cn(textWrapperVariants())}>
      <Text size="small" data-testid={suffixTestId('text', props)}>
        {textHighlights?.map((value, index) =>
          match(value.type)
            .with('text', () => value.text)
            .with('user', () => {
              const statusIndicator = isNotNil(value.id)
                ? props.statusIndicatorById?.get(value.id)
                : undefined;

              return (
                <span
                  key={value.text}
                  className={cn(mentionHighlightVariants())}
                  data-testid={suffixTestId(`user-${index}`, props)}
                >
                  {value.text}
                  <Show when={isNotNil(statusIndicator)}>
                    <StatusIndicatorDot statusIndicator={statusIndicator} />
                  </Show>
                </span>
              );
            })
            .with('link', () => (
              <Link
                key={value.text}
                size="small"
                href={value.text}
                target="_blank"
                title={value.text}
                data-testid={suffixTestId(`link-${index}`, props)}
              />
            ))
            .with('email', () => (
              <Link
                key={value.text}
                size="small"
                href={`mailto:${value.text}`}
                title={value.text}
                data-testid={suffixTestId(`email-${index}`, props)}
              />
            ))
            .exhaustive()
        )}
      </Text>
    </div>
  );
}
